/**
 * Conferma esplicita per la modifica dei dati fiscali di un cliente già
 * fatturato (R6-B).
 *
 * Il backend risponde 409 elencando le fatture emesse/pagate del cliente:
 * qui vengono mostrate, e su conferma il chiamante rinvia la stessa PATCH con
 * `conferma_modifica_fiscale: true`. L'autorità resta il backend — questa
 * modale è solo il canale con cui l'utente esprime la conferma.
 *
 * Condivisa fra le due schermate che scrivono l'anagrafica cliente
 * (dettaglio cliente e card cliente nel dettaglio richiesta).
 */
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from './ui/Modal';
import type { ConfermaModificaFiscaleDetail } from '../lib/modificaFiscale';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface Props {
  detail: ConfermaModificaFiscaleDetail;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfermaModificaFiscaleModal({ detail, isSaving, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('anagrafiche.modifica_fiscale.titolo')}
      subtitle={t('anagrafiche.modifica_fiscale.sottotitolo')}
      onClose={onCancel}
      maxWidth="max-w-xl"
      testid="conferma-modifica-fiscale-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors disabled:opacity-60"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            data-testid="conferma-modifica-fiscale-submit"
            className="px-3 py-1.5 text-xs font-medium text-white bg-accent-danger rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {isSaving ? t('common.azioni.salvataggio_in_corso') : t('anagrafiche.modifica_fiscale.conferma')}
          </button>
        </>
      }
    >
      <div className="flex gap-2.5 text-sm text-ink-dark">
        <AlertTriangle size={16} className="text-accent-warning flex-shrink-0 mt-0.5" />
        <p>{t('anagrafiche.modifica_fiscale.avvertenza')}</p>
      </div>

      {detail.campi.length > 0 && (
        <div>
          <p className="text-xs text-ink-soft mb-1">{t('anagrafiche.modifica_fiscale.campi_coinvolti')}</p>
          <p className="text-sm text-ink-dark font-medium">
            {detail.campi.map((c) => t(`anagrafiche.modifica_fiscale.campo.${c}`)).join(', ')}
          </p>
        </div>
      )}

      <div>
        <p className="text-xs text-ink-soft mb-1.5">
          {t('anagrafiche.modifica_fiscale.fatture_coinvolte', { count: detail.fatture.length })}
        </p>
        <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
          {detail.fatture.map((f) => (
            <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-mono text-ink-dark flex-1">{f.numero ?? '—'}</span>
              <span className="text-ink-soft text-xs">{formatDateIT(f.data)}</span>
              <span className="text-xs text-ink-mid capitalize">{f.stato}</span>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
